import React from "react";
import {
  Autocomplete,
  Chip,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

type FieldKey = "skills" | "categories";

interface Props {
  skills: string[];
  categories: string;
  categoryOptions?: { label: string; value: string }[];
  onChange: (key: FieldKey, values: string[] | string) => void;
}

const freeSoloProps = {
  freeSolo: true,
  multiple: true,
  filterSelectedOptions: true,
};

const SkillsAndTagsForm: React.FC<Props> = ({ skills, categories, categoryOptions = [], onChange }) => (
  <Stack spacing={3} sx={{ fontFamily: "'Lexend', sans-serif" }}>
    <TagBox
      label="Skills"
      value={skills}
      onChange={(v) => onChange("skills", v)}
    />
    <CategoryDropdown
      label="Categories"
      value={categories}
      categoryOptions={categoryOptions}
      onChange={(v) => onChange("categories", v)}
    />
  </Stack>
);

interface TagBoxProps {
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
}

interface CategoryDropdownProps {
  label: string;
  value: string;
  categoryOptions: { label: string; value: string }[];
  onChange: (v: string) => void;
}

/* Reusable Autocomplete field for skills */
const TagBox: React.FC<TagBoxProps> = ({ label, value, onChange }) => (
  <Autocomplete
    {...freeSoloProps}
    value={value}
    onChange={(_, v) => onChange(Array.isArray(v) ? v : [])}
    options={[]} // optionally: provide predefined list here
    renderTags={(v, getTagProps) =>
      v.map((option, idx) => {
        const tagProps = getTagProps({ index: idx });
        return (
          <Chip
            {...tagProps}
            label={option}
            sx={{
              backgroundColor: "rgba(255,255,255,0.1)",
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
            }}
          />
        );
      })
    }
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "rgba(255,255,255,0.06)",
            color: COLORS.text,
            "& fieldset": {
              borderColor: COLORS.border,
            },
            "&:hover fieldset": {
              borderColor: "rgba(255,255,255,0.2)",
            },
            "&.Mui-focused fieldset": {
              borderColor: COLORS.red,
            },
          },
          "& .MuiInputLabel-root": {
            color: COLORS.muted,
          },
        }}
      />
    )}
  />
);

/* Dropdown select for categories */
const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  label,
  value,
  categoryOptions,
  onChange,
}) => (
  <FormControl fullWidth>
    <InputLabel sx={{ color: COLORS.muted }}>{label}</InputLabel>
    <Select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      label={label}
      sx={{
        backgroundColor: "rgba(255,255,255,0.06)",
        color: COLORS.text,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: COLORS.border,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.2)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: COLORS.red,
        },
        "& .MuiSvgIcon-root": {
          color: COLORS.text,
        },
      }}
    >
      <MenuItem value="" sx={{ backgroundColor: COLORS.panel, color: COLORS.text }}>
        <em>Select a category</em>
      </MenuItem>
      {categoryOptions.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          sx={{
            backgroundColor: COLORS.panel,
            color: COLORS.text,
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

export default SkillsAndTagsForm;
